/*
 * MARKETING CONSENT — the smallest permissioned surface.
 *
 * Aggregate consent counts, and an export of currently consented, non-deleted members carrying
 * exactly four fields. There is no member directory here: no row is ever rendered, and the
 * server's projection is the boundary, not this component.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Mail } from "lucide-react";
import { Panel, Note, fmt } from "./ConsolePrimitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder`;

export const MarketingConsentView = () => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/marketing-consent`);
      setSummary(data);
    } catch (e) {
      setError("Marketing consent could not be read just now.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/marketing-consent/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `arvexis-marketing-consent-${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      load();
    } catch (e) {
      setError("The export was refused.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <header className="fc__section-head">
        <h2 data-testid="fc-marketing-title">Marketing Consent</h2>
        <p>Permission-based Arvexis marketing only. Consent is never inferred and never backfilled.</p>
      </header>
      <Panel title="Consent, as stored" icon={Mail} testId="fc-marketing-panel"
        sub="Absence of consent is no consent. Withdrawal is kept as evidence.">
        <table className="fc-table">
          <thead><tr><th>Metric</th><th>Value</th></tr></thead>
          <tbody>
            <tr data-testid="fc-marketing-consented-row">
              <td><b>Currently consented members</b></td>
              <td data-testid="fc-marketing-consented">{summary ? fmt(summary.consented) : "—"}</td>
            </tr>
            <tr data-testid="fc-marketing-withdrawn-row">
              <td><b>Withdrawn consent</b></td>
              <td data-testid="fc-marketing-withdrawn">{summary ? fmt(summary.withdrawn) : "—"}</td>
            </tr>
          </tbody>
        </table>
        <Note testId="fc-marketing-fields">
          {`Export carries only: ${(summary?.export_fields || []).join(", ") || "—"}. Deleted and pending-deletion members are excluded. No conversation, Talk, Dream, Shared Memory, continuity, circumstance, research, upload, location or usage data is part of this export.`}
        </Note>
        <div style={{ display: "flex", gap: 10, marginTop: 14, alignItems: "center" }}>
          <button className="fc-btn" onClick={exportCsv} disabled={exporting || !summary?.consented}
            data-testid="fc-marketing-export-btn">
            {exporting ? "Exporting…" : "Export consented members (CSV)"}
          </button>
          {!summary?.consented && (
            <span className="fc-metric__foot" data-testid="fc-marketing-empty">
              No member has consented yet.
            </span>
          )}
        </div>
        {error && <Note testId="fc-marketing-error">{error}</Note>}
      </Panel>
    </>
  );
};
